import { useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { useToast } from "@/hooks/use-toast";
import { LockedBanner } from "../common/LockedBanner";
import { PeriodSelector } from "@/components/workflow/common/PeriodSelector";
import { CountryCombobox, CountryDisplay } from "@/components/ui/country-combobox";

interface Props {
  isOpen: boolean;
}

export default function AccommodationFormSection({ isOpen }: Props) {
  const {
    scope3Data,
    assessment,
    currentAccommodationEntry,
    setCurrentAccommodationEntry,
    addAccommodationEntry,
    deleteAccommodationEntry,
    forceSave,
    isLocked,
    isReadOnly,
  } = useWizard();
  const { toast } = useToast();
  const disabled = isLocked || isReadOnly;
  const pendingSaveRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setCurrentAccommodationEntry({ period: null, country: undefined, countryCode: undefined });
    }
  }, [isOpen]);

  useEffect(() => {
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      forceSave();
    }
  }, [scope3Data.accommodationEntries, forceSave]);

  const totalTco2e = useMemo(() => {
    return scope3Data.accommodationEntries.reduce((sum, entry) => sum + (entry.tco2e ?? 0), 0);
  }, [scope3Data.accommodationEntries]);

  const handleAddEntry = async () => {
    const result = await addAccommodationEntry();
    if (!result.ok) {
      toast({ title: result.error || "Failed to add entry", variant: "destructive" });
      return;
    }
    pendingSaveRef.current = true;
    toast({
      title: "Entry added",
      description: result.tco2e != null ? `${result.tco2e.toFixed(3)} tCO₂e` : undefined,
    });
  };

  return (
    <div className="space-y-6">
      {disabled && <LockedBanner />}

      <div className="form-surface-card">
        <div className="flex flex-col gap-6 p-6">
          <h4 className="text-sm font-bold leading-5 text-gray-800">Add Hotel Stay</h4>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label className="form-label">Hotel Nights</Label>
              <Input
                className="form-input"
                type="number"
                min={0}
                step="1"
                value={currentAccommodationEntry.hotelNights ?? ""}
                onChange={(e) =>
                  setCurrentAccommodationEntry((prev) => ({
                    ...prev,
                    hotelNights: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
                placeholder="e.g. 5"
                disabled={disabled}
              />
            </div>
            <div className="space-y-2">
              <Label className="form-label">Country</Label>
              <CountryCombobox
                value={currentAccommodationEntry.country || ""}
                onValueChange={(name) =>
                  setCurrentAccommodationEntry((prev) => ({ ...prev, country: name }))
                }
                onCodeChange={(code) =>
                  setCurrentAccommodationEntry((prev) => ({ ...prev, countryCode: code }))
                }
                placeholder="Select country..."
                disabled={disabled}
              />
            </div>
            <PeriodSelector
              periodStart={assessment!.periodStart}
              periodEnd={assessment!.periodEnd}
              value={currentAccommodationEntry.period ?? null}
              onChange={(value) => setCurrentAccommodationEntry((prev) => ({ ...prev, period: value }))}
              disabled={disabled}
            />
          </div>

          <div>
            <Button onClick={handleAddEntry} disabled={disabled} className="form-add-button">
              <Plus className="mr-2 h-3 w-3" /> Add Entry
            </Button>
          </div>
        </div>
      </div>

      <div className="entries-card">
        <div className="entries-header">
          <h4 className="entries-title">Entries</h4>
          <span className="entries-count">{scope3Data.accommodationEntries.length} entries</span>
        </div>

        {scope3Data.accommodationEntries.length === 0 ? (
          <p className="entries-empty">No entries yet.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="table-row-base">
                <TableHead className="table-head-base">Hotel Nights</TableHead>
                <TableHead className="table-head-base">Country</TableHead>
                <TableHead className="table-head-base text-center">Period</TableHead>
                <TableHead className="table-head-base text-center">tCO₂e</TableHead>
                <TableHead className="table-head-base text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scope3Data.accommodationEntries.map((entry) => (
                <TableRow key={entry.id} className="table-row-base">
                  <TableCell className="table-cell-base-strong">{entry.hotelNights} nights</TableCell>
                  <TableCell className="table-cell-base">
                    {entry.country ? <CountryDisplay value={entry.country} /> : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.period ? (entry.period.granularity === "annually" ? "Annual" : entry.period.period) : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-center">
                    {entry.calculating
                      ? "Calculating..."
                      : entry.tco2e != null
                        ? entry.tco2e.toFixed(3)
                        : "—"}
                  </TableCell>
                  <TableCell className="table-cell-base-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        deleteAccommodationEntry(entry.id);
                        pendingSaveRef.current = true;
                      }}
                      className="delete-button"
                      disabled={disabled}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="total-card">
        <p className="total-label">Total accommodation emissions</p>
        <p className="total-value">{totalTco2e.toFixed(3)} tCO₂e</p>
      </div>

      <p className="text-xs leading-4 text-slate-500">
        Emissions calculated using country-specific hotel stay factors (CSHB 2023). Countries not in the dataset show —.
      </p>
    </div>
  );
}
